import { IdValue } from '../../../shared/models/id-value.model';
import { mapToIdValues } from '../../../shared/utils';
import { DpFactorType, MICompany, MortgageType, PmiType, PurposeOfLoan, UpfrontMiType } from '../loan-information.model';
import { OriginationChannel } from '../origination-channel.enum';
import { ProductDescription } from '../product-description.enum';

export const affordableLoanOptions = [new IdValue(0, true, 'Yes'), new IdValue(1, false, 'No')];
export const yesNoOptions = [new IdValue(0, true, 'Yes'), new IdValue(1, false, 'No')];

export const fundSourceTypeOptions = [
  new IdValue(0, 0, 'Select'),
  new IdValue(0, 1, 'Community Non Profit'),
  new IdValue(0, 2, 'Federal Agency'),
  new IdValue(0, 3, 'Institutional'),
  new IdValue(0, 4, 'Lender'),
  new IdValue(0, 5, 'Local Agency'),
  new IdValue(0, 6, 'Non Parent Relative'),
  new IdValue(0, 7, 'Non Profit Instrumentality of Government'),
  new IdValue(0, 8, 'Parent'),
  new IdValue(0, 9, 'Relative'),
  new IdValue(0, 10, 'Religious Non Profit'),
  new IdValue(0, 11, 'State Agency'),
  new IdValue(0, 12, 'Unmarried Partner'),
  new IdValue(0, 13, 'Unrelated Friend'),
  new IdValue(0, 14, 'Employer'),
  new IdValue(0, 15, 'Property Seller'),
];

export const organizationChannelOptions = [
  new IdValue(1, OriginationChannel.retail, 'Retail'),
  new IdValue(2, OriginationChannel.brokeredOut, 'Brokered Out'),
];
export const hasPrepaymentPenaltyOptions = [new IdValue(0, 1, 'Yes'), new IdValue(1, 2, 'No')];
export const productDescriptions = [
  new IdValue(0, ProductDescription.select, 'Select'),
  new IdValue(1, ProductDescription.standardLCOR, 'Standard LCOR'),
  new IdValue(2, ProductDescription.affordableLTV, 'Affordable LTV'),
  new IdValue(60, ProductDescription.other, 'Other'),
];

export enum RefinancePurpose {
  None = 0,
  CashOutDebtConsolidation = 1,
  CashOutHomeImprovement = 2,
  CashOutLimited = 3,
  CashOutOther = 4,
  NoCashOutFHAStreamlinedRefinance = 5,
  NoCashOutFREOwnedRefinance = 6,
  NoCashOutOther = 7,
  NoCashOutStreamlinedRefinance = 8,
  ChangeInRateTerm = 9,
}

export const otherRefinancePurposeOptions = [
  new IdValue(RefinancePurpose.None, RefinancePurpose.None, 'Select'),
  new IdValue(RefinancePurpose.CashOutDebtConsolidation, RefinancePurpose.CashOutDebtConsolidation, 'Cash-Out Debt Consolidation'),
  new IdValue(RefinancePurpose.CashOutHomeImprovement, RefinancePurpose.CashOutHomeImprovement, 'Cash-Out Home Improvement'),
  new IdValue(RefinancePurpose.CashOutLimited, RefinancePurpose.CashOutLimited, 'Limited Cash Out'),
  new IdValue(RefinancePurpose.CashOutOther, RefinancePurpose.CashOutOther, 'Cash-Out Other'),
  new IdValue(
    RefinancePurpose.NoCashOutFHAStreamlinedRefinance,
    RefinancePurpose.NoCashOutFHAStreamlinedRefinance,
    'No Cash-Out FHA Streamlined Refinance',
  ),
  new IdValue(RefinancePurpose.NoCashOutFREOwnedRefinance, RefinancePurpose.NoCashOutFREOwnedRefinance, 'No Cash-Out FRE Owned Refinance'),
  new IdValue(RefinancePurpose.NoCashOutOther, RefinancePurpose.NoCashOutOther, 'No Cash-Out Other'),
  new IdValue(RefinancePurpose.NoCashOutStreamlinedRefinance, RefinancePurpose.NoCashOutStreamlinedRefinance, 'No Cash-Out Streamlined Refinance'),
  new IdValue(RefinancePurpose.ChangeInRateTerm, RefinancePurpose.ChangeInRateTerm, 'Change in Rate Term'),
];

export const cashOutRefinancePurposeOptions = [
  new IdValue(0, 0, 'Select'),
  new IdValue(1, 1, 'Cash-Out Debt Consolidation'),
  new IdValue(2, 2, 'Cash-Out Home Improvement'),
  new IdValue(4, 4, 'Cash-Out Other'),
];

export const fhaRefinancePurposeOptions = [
  new IdValue(0, 0, 'Select'),
  { ...new IdValue(1, 1, 'Streamlined With Appraisal'), isHidden: true },
  new IdValue(2, 2, 'Streamlined without Appraisal'),
  new IdValue(3, 3, 'No Cash-Out'),
  new IdValue(4, 4, 'Cash-Out'),
];

export enum RefinancePurposeFha {
  None = 0,
  StreamlinedWithAppraisal = 1,
  StreamlinedWithoutAppraisal = 2,
  NoCashOut = 3,
  CashOut = 4,
  SimpleRefinance = 5,
}

export const vaRefinancePurposeOptions = mapToIdValues(['Select', 'IRRRL', 'Other'], false);

export enum RefinancePurposeVa {
  None = 0,
  IRRRL = 1,
  Other = 2,
}

export const loanProgramCodesVAForIRRR = [
  '4101 IRRL',
  '4103 IRRL',
  '4125 IRRL',
  '4129 IRRL',
  '4102 IRRL',
  '4104 IRRL',
  '4131 IRRL',
  '4131HBIRRL',
  '4151 IRRL',
  '4151HBIRRL',
];

export const upfrontMiTypeOptions = [new IdValue(0, UpfrontMiType.financed, 'Financed'), new IdValue(1, UpfrontMiType.notfinanced, 'Not Financed')];

export const purposeOfLoanOptions = [
  new IdValue(0, PurposeOfLoan.select, 'Select'),
  new IdValue(1, PurposeOfLoan.purchase, 'Purchase'),
  new IdValue(2, PurposeOfLoan.refinance, 'Refinance'),
  new IdValue(3, PurposeOfLoan.construction, 'Construction'),
  new IdValue(4, PurposeOfLoan.constructionPerm, 'Construction Perm'),
  new IdValue(7, PurposeOfLoan.purchaseMoneySecond, 'Second - Purchase Money'),
  new IdValue(10, PurposeOfLoan.refiSecond, 'Second - Refinance'),
  new IdValue(5, PurposeOfLoan.second, 'Second'),
  new IdValue(9, PurposeOfLoan.purchaseMoneyThird, 'Third - Purchase Money'),
  new IdValue(11, PurposeOfLoan.refiThird, 'Third - Refinance'),
  new IdValue(6, PurposeOfLoan.third, 'Third'),
  new IdValue(8, PurposeOfLoan.other, 'Other'),
];

export const pmiTypeOptions = [
  new IdValue(0, PmiType.none, 'None'),
  new IdValue(1, PmiType.monthly, 'Monthly'),
  new IdValue(2, PmiType.standard, 'Standard Annual'),
  new IdValue(3, PmiType.singlePremRefund, 'Single Prem. Refundable'),
  new IdValue(4, PmiType.singlePremNonRefund, 'Single Prem. Non-Refundable'),
  new IdValue(5, PmiType.lenderPaid, 'Lender Paid'),
  new IdValue(6, PmiType.splitPremNonRefund, 'Split Prem. Non-Refundable'),
];

export const miCompanies = [
  new IdValue(null, MICompany.archMI, 'Arch MI'),
  new IdValue(null, MICompany.quoteWithEligibility, 'Essent MI - Quote with Eligibility'),
  new IdValue(null, MICompany.enact, 'Enact'),
  new IdValue(null, MICompany.mgic, 'MGIC'),
  new IdValue(null, MICompany.nationalQuoteWithEligiblity, 'National MI - Quote with Eligiblity'),
  new IdValue(null, MICompany.radian, 'Radian'),
];

export const mortgageAppliedForOptions = [
  new IdValue(0, MortgageType.notAssigned, 'Select'),
  new IdValue(1, MortgageType.va, 'VA'),
  new IdValue(2, MortgageType.fha, 'FHA'),
  new IdValue(3, MortgageType.conventional, 'Conventional'),
  new IdValue(4, MortgageType.usda, 'USDA'),
  new IdValue(5, MortgageType.other, 'Other'),
  new IdValue(6, MortgageType.heloc, 'HELOC'),
  new IdValue(7, MortgageType.stateAgency, 'State Agency'),
];

export const mortgageAppliedForOptionsNoSelect = mortgageAppliedForOptions.filter((o) => o.value !== MortgageType.notAssigned);

export const qualRateOptions = mapToIdValues(
  [
    'Select',
    'Note Rate',
    'Max Rate in First 5 Years',
    'Rate+2 or Unrounded Fully Indexed Rate',
    'Rate or Unrounded Fully Indexed Rate',
    'Rate+1',
    'Rate+2 or Rounded Fully Indexed Rate',
    'Rate or Rounded Fully Indexed Rate',
  ],
  false,
);

export const intOnlyQualPaymentOptions = mapToIdValues(
  ['Default', 'Amortizing Over Full Term', 'Amortizing Over Non Interest Only Term', 'Interest Only'],
  false,
);

export const sectionOfActOptions = mapToIdValues(
  ['Select', '203(b)', '203(b)2', '203(b)/251', '203(k)', '203(k)/251', '221(d)(2)', '221(d)(2)/251', '234(C)', '234(C)/251', '184'],
  false,
);

export const amortizationTypeOptions = [
  new IdValue(0, 0, 'Fixed'),
  new IdValue(1, 1, 'ARM'),
  new IdValue(3, 3, 'GPM'),
  new IdValue(4, 4, 'No Payment'),
  new IdValue(5, 5, 'Custom Product'),
];

export enum AmortizationType {
  NotAssigned = null,
  Fixed = 0,
  ARM = 1,
  GPM = 3,
  NoPayment = 4,
  CustomProduct = 5,
}

//AIO state specific qualeRateOptions
export const aioStates = ['IL', 'MD', 'MN', 'NM', 'PA'];

export const dpFactorOptions = [
  new IdValue(0, DpFactorType.select, 'Select, Enter Base Loan'),
  new IdValue(1, DpFactorType.downpaymentPercentage, 'Down Payment Percentage (%)'),
  new IdValue(3, DpFactorType.downpaymentAmount, 'Down Payment Amount ($)'),
];

export const caseAssignmentDateOptions = mapToIdValues(
  [
    'Before 7/14/2008',
    '7/14/2008 - 9/30/2008',
    '10/1/2008 - 12/31/2008',
    '01/01/2009 - 04/04/2010',
    '4/5/2010 - 10/3/2010',
    '10/4/2010 - 4/17/2011',
    '4/18/2011 - 4/8/2012',
    '4/9/2012 - 6/10/2012',
    '6/11/2012 - 3/31/2013',
    '4/1/2013 - 6/2/2013',
    '6/3/2013 - 1/25/2015',
    '01/26/2015 - 09/30/2016',
    '10/01/2016 - 1/26/2017',
    '1/27/2017 - 8/31/2019',
    '9/1/2019 - 3/19/2023',
    'On or After 3/20/2023',
  ],
  false,
);

export const gusRunFirstDateOptions = mapToIdValues(
  [
    'On or Before 09/30/2011',
    '10/1/2011 - 12/6/2011',
    '12/7/2011 - 9/30/2012',
    '10/1/2012 - 9/30/2014',
    '10/1/2014 - 9/30/2015',
    '10/01/2015 To 09/30/2016',
    'On or After 10/01/2016 ',
  ],
  false,
);

export const fundingFeeTypeOptions = mapToIdValues(
  [
    'Select',
    'Regular Military First Use',
    'Regular Military Subsequent Use',
    'Reserves or Guard First Use',
    'Reserves or Guard Subsequent Use',
    'Interest Rate Reduction Refi',
    'Manufactured Home',
    'Loan Assumption',
    'Exempt',
  ],
  false,
);

export const referralTypes = [
  new IdValue(null, null, 'Select'),
  new IdValue(0, 'Builder', 'Builder'),
  new IdValue(1, 'MSA', 'MSA'),
  new IdValue(2, 'DeskRental', 'Desk Rental'),
  new IdValue(3, 'Realtor', 'Realtor'),
  new IdValue(4, 'Past Customer', 'Past Customer'),
  new IdValue(5, 'Financial Planner', 'Financial Planner'),
  new IdValue(6, 'Leadshare', 'Leadshare'),
  new IdValue(7, 'Other', 'Other'),
  new IdValue(8, 'NotApplicable', 'N/A'),
];

export const originationChannelOptions = [
  { label: 'Retail', value: 'Retail' },
  { label: 'Brokered Out', value: 'BrokeredOut' },
];
export interface LoanReferralsModel {
  referralType: string;
  referralCompany: string;
  referralContactName: string;
  referralContactNumber: string;
  referralEmailAddress: string;
  referralAddress: string;
  referralCity: string;
  referralState: string;
  referralZipCode: string;
  referralPartner: string;
  relocationSourceType: string;
}

export interface LicensingAgencies {
  code: string;
  name: string;
  state: string;
}

export interface AdditionalProdAssistants {
  addlProdAssistants: IdValue<string, string>[];
  addlProdAssistants2: IdValue<string, string>[];
}

export const texasA6StatusOptions = [
  new IdValue(0, null, 'Select'),
  new IdValue(1, 'Non A6 to Non A6', 'Non A6 to Non A6'),
  new IdValue(2, 'Non A6 to A6', 'Non A6 to A6'),
  new IdValue(3, 'A6 to Non A6', 'A6 to Non A6'),
  new IdValue(4, 'A6 to A6', 'A6 to A6'),
];
